import {
  filterContractVersionsByCurrent,
  getUpcomingContractVersions,
} from 'modules/contracts/lib/utils'
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

interface Props {
  shiftsRow: ShiftsRow
  contractVersions: ContractVersions
}

interface Result {
  workingHours: number
  frequency: string
  workingWeekDays: string
}

const useContractDetails = ({ shiftsRow, contractVersions }: Props): Result => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ view }] = useSchedule()

  const referenceContract = shiftsRow.employee?.referenceContract
  if (
    hasFeatureFlag(ShiftManagementFeatureFlags.DEV_SHIFT_MANAGEMENT_GRAPHQL_MIGRATION) &&
    referenceContract
  ) {
    return {
      workingHours: referenceContract.workingHours / 100,
      frequency: referenceContract.workingHoursFrequency,
      workingWeekDays: referenceContract.workingWeekDays,
    }
  }

  // TODO: Remove this once 'dev_shift_management_graphql_migration' is done to use referenceContract from the employee.

  // When building ShiftRows for employee view, the row id is the employee id
  const contractVersionsByEmployee = contractVersions.filter({
    employee_id: shiftsRow.id,
  })

  // As mentioned in the Contract Versions collection,
  // this method only returns 1 version at the most.
  // Also, we will always display the information that was effective on
  // the first day of the current view, even if it changes mid-period.
  const currentContractVersion = filterContractVersionsByCurrent(
    contractVersionsByEmployee,
    view.firstDay
  )[0]

  // If employee is new hire with no current version,
  // we will need the upcoming version
  const upcomingContractVersion = getUpcomingContractVersions(
    contractVersionsByEmployee,
    view.firstDay
  )

  const contractVersion = currentContractVersion || upcomingContractVersion

  return {
    workingHours: (contractVersion?.attributes.working_hours ?? 0) / 100,
    frequency: contractVersion?.attributes.working_hours_frequency ?? '',
    workingWeekDays: contractVersion?.attributes.working_week_days ?? '',
  }
}

export default useContractDetails
